import { Box, Card, Icon, Paragraph, TextButton } from '@zillow/constellation-10';
import { IconChevronRightOutline } from '@zillow/constellation-icons';

import { LoadingParagraph, LoadingText } from '@zg-rentals/loading-components';

import type { Tour } from '../../../../constants/UpcomingToursConstants';

type BookedToursSectionProps = {
  inquiriesPath: string;
  onCalendarClick: () => void;
  tours: Array<Tour>;
  tourCalendarPath: string;
  isLoading: boolean;
  eventId?: string;
};

const BookedToursSection = ({
  inquiriesPath,
  onCalendarClick,
  tours,
  tourCalendarPath,
  isLoading,
  eventId,
}: BookedToursSectionProps) => {
  const buttonText = tours.length > 3 ? `+ ${tours.length - 3} more tours` : 'View calendar';
  const calendarButton = (
    <LoadingText isLoading={isLoading} loadingWidth="100%">
      <TextButton textStyle="body-sm" asChild>
        <a
          data-testid="UpcomingTours-calendarButton"
          onClick={onCalendarClick}
          href={tourCalendarPath}
          data-eventid={eventId}
        >
          {buttonText}
        </a>
      </TextButton>
    </LoadingText>
  );

  if (tours.length === 0) {
    return (
      <Box css={{ display: 'flex', flexDirection: 'column', gap: { base: 'default', lg: 'loose' } }}>
        <Card data-testid="UpcomingTours-noToursCard" elevated={false} tone="neutral">
          <LoadingParagraph
            isLoading={isLoading}
            loadingWidth="100%"
            css={{ textStyle: { base: 'body-sm-bold', lg: 'body-bold' } }}
          >
            No tours for this date
          </LoadingParagraph>
          <LoadingParagraph isLoading={isLoading} loadingWidth="100%" textStyle="body-sm">
            Booked tours will appear here.
          </LoadingParagraph>
        </Card>
        <Box>{calendarButton}</Box>
      </Box>
    );
  }

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: { base: 'default', lg: 'loose' } }}>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
        {tours.slice(0, 3).map((tour: Tour) => (
          <Card.Root
            key={tour.tourId}
            data-testid="UpcomingTours-tourCard"
            elevated={false}
            css={{
              borderLeft: '3px solid',
              borderLeftColor: 'bg.accent.brand.hero',
              cursor: 'pointer',
              _hover: {
                border: '1px solid',
                borderColor: 'blue.400',
              },
            }}
          >
            <Card.BlockAnchor
              href={`${inquiriesPath}?conversationId=${tour.conversationId}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="UpcomingTours-tourCard-blockAnchor"
            />
            <Card.Content>
              <Box
                css={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Box>
                  <LoadingParagraph isLoading={isLoading} loadingWidth="100%">
                    <Paragraph css={{ textStyle: { base: 'body-sm-bold', lg: 'body-bold' } }}>
                      {tour.tourName}
                    </Paragraph>
                  </LoadingParagraph>
                  <LoadingParagraph isLoading={isLoading} loadingWidth="100%">
                    <Paragraph textStyle="body-sm">{tour.tourTime}</Paragraph>
                  </LoadingParagraph>
                </Box>
                <Icon size="md" css={{ color: 'border.subtle' }} render={<IconChevronRightOutline />} />
              </Box>
            </Card.Content>
          </Card.Root>
        ))}
      </Box>

      <Box>{calendarButton}</Box>
    </Box>
  );
};

export default BookedToursSection;
